import ClaimsMatcherModal from "./ClaimsMatcherModal";
import * as support from "./ClaimsTable.support";
import {
  detectSimpleDuplicates,
  getClaimId,
  buildDifferencesForDuplicateGroup,
  parseClaimId,
} from "./claimsMatching.support";
import {
  ArrowDownwardRounded as SortedDescendingIcon,
  ArrowUpwardRounded as SortedAscendingIcon,
  LockOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Grid,
  Tooltip,
  Stack,
  TableRow,
  TableCell,
} from "@mui/material";
import * as logger from "common/logger";
import Component from "components/Component";
import DefaultLoader from "components/common/DefaultLoader";
import OpenClosedBadge from "components/common/OpenClosedBadge";
import Options from "components/common/Options";
import PaginatedTable from "components/common/PaginatedTable";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import TextNote from "components/common/TextNote";
import * as labels from "config.labels";
import * as flags from "flags";
import { useState, Fragment, useCallback, useMemo } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { useDetectDuplicatesMutation } from "services/aiAgents";
import * as claimsService from "services/claimsService";
import * as staticDataService from "services/staticDataService";
import * as claimsActions from "store/actions/input/claims/claimsActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import useDebouncedValue from "store/hooks/useDebouncedValue";
import * as claimsSelectors from "store/selectors/input/claims/claimsSelectors";
import * as utils from "utils";

const isExcluded = (exclusions, row) => {
  const excluded = exclusions?.[row.key]?.includes(row.index) || false;
  const noCoverage = row._COVERAGE == null;
  return excluded || noCoverage;
};

const floatAsDollars = (value) => {
  if (value == null) {
    return "";
  }
  return "$" + value.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

// The table loads the whole book up to this many claims (largest first): the same cap
// as the All Claims page (AllClaimsQuery.limit in services/claims/models.py). Large
// results arrive gzipped via a presigned URL: 20,000 claims is ~1.4 MB to download and
// ~11 ms to re-sort per render, while real books run to a few thousand (largest seen 13,875).
const CLAIMS_TABLE_MAX_ROWS = 20_000;
// The exact-match duplicate check is local, so it covers every loaded claim. The AI call
// keeps to the largest 100 (what it was given when the table loaded only 100 rows).
const AI_DUPLICATE_DETECTION_MAX_CLAIMS = 100;

const useAllClaimsYears = ({ claimsQuery }) => {
  const lossYears = useSelector(
    (state) => state?.input?.claims?.lossYears ?? []
  );
  const { data: summaryCharts } = claimsService.useSummaryChartQuery(
    claimsQuery
  );

  return support.makeClaimsYears({ summaryCharts, lossYears });
};

const ClaimsTable = (props) => {
  const dispatch = useDispatch();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const claimsQuery = useDebouncedValue(
    useSelector(claimsSelectors.selectInputClaims)
  );
  const lossYears = useSelector(
    (state) => state.input?.claims?.lossYears || []
  );
  const claimsFiles = useSelector(claimsSelectors.selectDownloadFiles);

  const sourceDetails = {};
  (claimsFiles || []).forEach((file) => {
    sourceDetails[file.claimsKey] = {
      file: file.prettyName,
      sheet: file.sheetName,
    };
  });

  const {
    data: claims,
    isFetching: isClaimsJsonLoading,
    error: claimsError,
  } = claimsService.useClaimsQuery({
    claimsQuery,
    limit: CLAIMS_TABLE_MAX_ROWS,
    years: lossYears,
    includeExcluded: true,
  });

  const [isShowingComments, setIsShowingComments] = useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [
    detectDuplicates,
    { isLoading: isDetectingDuplicates },
  ] = useDetectDuplicatesMutation();

  const duplicateGroups = useSelector(
    (state) => state.input?.claims?.duplicateGroups || []
  );
  const acceptedDuplicates = useSelector(
    (state) => state.input?.claims?.acceptedDuplicates || []
  );
  const ignoredDuplicates = useSelector(
    (state) => state.input?.claims?.ignoredDuplicates || []
  );

  const rows = (claims ?? []).map((row, index) => {
    return { id: index, ...row };
  });

  const inMerge = (row) => {
    return props.mergedClaims
      .filter((ki) => ki.key === row.key && ki.index === row.index)
      .reduce(() => true, false);
  };
  const withCoverageList =
    props.createCoverageList &&
    rows.filter((r) => r["_COVERAGE_LIST"] && r["_COVERAGE_LIST"].length > 1)
      .length > 0;

  const isOpenClosedEditable = staticDataService.useFeatureFlag(
    "ui/enableEditOfClaimsOpenClosed",
    { default: false }
  );

  const isDuplicateDetectionEnabled = staticDataService.useFeatureFlag(
    flags.AI_CLAIMS_DUPLICATE_DETECTION,
    { default: false }
  );

  const isReportedLossOverridesEnabled = staticDataService.useFeatureFlag(
    "claims/reportedLossOverrides",
    { default: false }
  );

  const allClaimsYears = useAllClaimsYears({ claimsQuery });

  const yearOptions = allClaimsYears.map((year) => {
    const yearInt = parseInt(year.name);
    const selected = props.lossYears.includes(yearInt);
    return {
      id: yearInt,
      label: year.name,
      tip: `${year.name} (${year.value} losses)`,
      selected: selected,
    };
  });

  const formatCoverage = (row) => {
    if (!withCoverageList) return row;
    if (row["_COVERAGE_LIST"] && row["_COVERAGE_LIST"].length > 1) {
      const comb =
        row["_COVERAGE"] + "(" + row["_COVERAGE_LIST"].join(", ") + ")";
      return { ...row, _COVERAGE: comb };
    } else return row;
  };

  const formattedCoverage = rows.map((r) => formatCoverage(r));

  const runDuplicateDetection = useCallback(async () => {
    const claimsToCheck = claims ?? [];
    if (claimsToCheck.length === 0) return;

    const toDisplayClaim = (claim) => ({
      date: claim._DATE || "",
      claimant_name: claim._CLAIMANT_NAME || null,
      description: claim._DESCRIPTION || null,
      coverage: claim._COVERAGE || null,
      total_loss: claim._TOTAL_LOSS || null,
      open_closed: claim._OPEN_CLOSED || null,
    });

    const simpleGroupsRaw = detectSimpleDuplicates(claimsToCheck);
    const simpleClaimIds = new Set(
      simpleGroupsRaw.flatMap((g) => g.claim_ids || [])
    );
    const claimsById = new Map(claimsToCheck.map((c) => [getClaimId(c), c]));

    const simpleGroups = simpleGroupsRaw
      .map((g) => {
        const claimIds = g.claim_ids || [];
        const uniqueClaimIds = [...new Set(claimIds)];
        if (uniqueClaimIds.length < 2) {
          logger.error("Duplicate group has less than 2 unique claim IDs:", {
            claimIds,
            uniqueClaimIds,
          });
          return null;
        }

        const allClaims = uniqueClaimIds
          .map((id) => claimsById.get(id))
          .filter(Boolean)
          .map((claim) => toDisplayClaim(claim));

        const {
          field_differences,
          non_conforming_fields,
          overall_reasoning,
        } = buildDifferencesForDuplicateGroup(allClaims);

        const match_confidence = field_differences.length === 0 ? 1.0 : 0.95;

        return {
          ...g,
          claim_ids: uniqueClaimIds,
          current_claim_ids: uniqueClaimIds,
          prior_claim_ids: [],
          match_confidence,
          field_differences,
          non_conforming_fields,
          overall_reasoning,
        };
      })
      .filter(Boolean);

    const remainingClaims = claimsToCheck
      .slice(0, AI_DUPLICATE_DETECTION_MAX_CLAIMS)
      .filter((c) => !simpleClaimIds.has(getClaimId(c)));
    if (remainingClaims.length > 1) {
      try {
        const aiClaims = remainingClaims.map((claim) => ({
          claim_id: getClaimId(claim),
          ...toDisplayClaim(claim),
        }));

        const result = await detectDuplicates({ claims: aiClaims }).unwrap();

        const aiGroups = result.results.map((group) => ({
          ...group,
          claim_ids: group.claim_ids || [],
          current_claim_ids: group.claim_ids || [],
          prior_claim_ids: [],
          field_differences: (group.field_differences || []).map((d) => ({
            field_name: d.field_name,
            previous_value: d.previous_value ?? d.value_1 ?? null,
            current_value: d.current_value ?? d.value_2 ?? null,
            difference_type: d.difference_type || "data_correction",
            confidence: d.confidence ?? 0.9,
            reasoning: d.reasoning || `Values differ for ${d.field_name}`,
          })),
          non_conforming_fields: (group.field_differences || []).map(
            (d) => d.field_name
          ),
        }));

        dispatch(
          claimsActions.setDuplicateGroups([...simpleGroups, ...aiGroups])
        );
      } catch (error) {
        logger.error("AI duplicate detection failed:", error);
        dispatch(claimsActions.setDuplicateGroups(simpleGroups));
      }
    } else {
      dispatch(claimsActions.setDuplicateGroups(simpleGroups));
    }
  }, [claims, detectDuplicates, dispatch]);

  const handleApplyDuplicates = (accepted) => {
    dispatch(claimsActions.updateAcceptedDuplicates(accepted));

    const newIgnoredIds = new Set();
    const acceptedGroupKeys = new Set(accepted.map((a) => a.group_key));

    duplicateGroups.forEach((group) => {
      const claimIds = group.claim_ids || group.current_claim_ids || [];
      const isAccepted = acceptedGroupKeys.has(group.group_key);

      if (isAccepted) {
        const { key, index } = parseClaimId(claimIds[0]);
        if (key && index !== undefined) {
          props.toggleClaimExclude(key, index, false);
        }

        claimIds.slice(1).forEach((claimId) => {
          newIgnoredIds.add(String(claimId));
          const { key, index } = parseClaimId(claimId);
          if (key && index !== undefined) {
            props.toggleClaimExclude(key, index, true);
          }
        });
      } else {
        claimIds.forEach((claimId) => {
          const { key, index } = parseClaimId(claimId);
          if (key && index !== undefined) {
            props.toggleClaimExclude(key, index, false);
          }
        });
      }
    });

    dispatch(claimsActions.setIgnoredDuplicates(Array.from(newIgnoredIds)));
    setIsReviewDialogOpen(false);
  };

  const visibleDuplicateGroups = useMemo(() => {
    return duplicateGroups
      .map((group) => ({
        ...group,
        claim_ids: (group.claim_ids || []).filter(
          (id) => !ignoredDuplicates.includes(String(id))
        ),
      }))
      .filter((group) => group.claim_ids.length > 1);
  }, [duplicateGroups, ignoredDuplicates]);

  const hasDuplicates = visibleDuplicateGroups.length > 0;
  const hasAppliedDuplicates = acceptedDuplicates.length > 0;

  const handleEditDuplicates = () => {
    setIsReviewDialogOpen(true);
  };

  const duplicateModalResults = useMemo(() => {
    if (duplicateGroups.length === 0) return null;
    return {
      results: duplicateGroups,
    };
  }, [duplicateGroups]);

  const currentClaimsForModal = useMemo(() => {
    return (claims || []).map((claim) => {
      const id = getClaimId(claim);
      return {
        claim_id: id,
        date: claim._DATE_OF_LOSS || claim._DATE || "",
        claimant_name: claim._CLAIMANT_NAME || null,
        description: claim._DESCRIPTION || null,
        coverage: claim._COVERAGE || null,
        total_loss: claim._TOTAL_LOSS || null,
        open_closed: claim._OPEN_CLOSED || null,
      };
    });
  }, [claims]);

  return (
    <Fragment>
      {isDuplicateDetectionEnabled && (
        <ClaimsMatcherModal
          open={isReviewDialogOpen}
          onClose={() => setIsReviewDialogOpen(false)}
          matchResults={duplicateModalResults}
          onApply={handleApplyDuplicates}
          priorClaims={[]}
          currentClaims={currentClaimsForModal}
          initialAcceptedMatches={acceptedDuplicates}
          isDuplicateMode={true}
          title={"Review Duplicate Claims"}
        />
      )}
      <Component
        title={
          <Grid
            container
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Grid
              item
              style={{ height: props.mergingClaims ? "80px" : "50px" }}
            >
              <div style={{ marginTop: "13px" }}>
                {translateLabel(labels.CLAIMS_TABLE_TOP_CLAIMS, {
                  default: "Top Claims",
                })}
              </div>
              {props.mergingClaims ? (
                <Button
                  variant={"contained"}
                  sx={{ marginTop: "10px" }}
                  onClick={props.merge}
                  disableElevation
                >
                  {translateLabel(labels.CLAIMS_TABLE_MERGE_SELECTED, {
                    default: "Merge Selected",
                  })}
                </Button>
              ) : (
                ""
              )}
            </Grid>

            {isClaimsJsonLoading && (
              <Grid item>
                <DefaultLoader
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginLeft: "8px",
                    marginRight: "auto",
                  }}
                  size={14}
                />
              </Grid>
            )}
          </Grid>
        }
        options={
          <Stack direction={"row"} justifyContent={"space-between"}>
            <div style={{ maxWidth: "85%" }}>
              <Options
                items={yearOptions}
                onClick={({ id, selected }) => {
                  (selected ? props.removeLossYear : props.addLossYear)(id);
                }}
                variant={"button"}
              />
            </div>
            <Stack direction={"row"} spacing={1}>
              {isDuplicateDetectionEnabled && (
                <Button
                  variant={"contained"}
                  color={"primary"}
                  size={"small"}
                  sx={{ height: 40, minWidth: 160 }}
                  onClick={runDuplicateDetection}
                  disabled={
                    isDetectingDuplicates || !claims || claims.length === 0
                  }
                  disableElevation
                >
                  {isDetectingDuplicates ? "Detecting..." : "Detect Duplicates"}
                </Button>
              )}
              <Button
                variant={"outlined"}
                sx={{ height: 40, color: "primary.main" }}
                onClick={() => {
                  setIsShowingComments((currentState) => !currentState);
                }}
                disableElevation
              >
                {isShowingComments
                  ? translateLabel(labels.CLAIMS_TABLE_HIDE_NOTES, {
                      default: "Hide Notes",
                    })
                  : translateLabel(labels.CLAIMS_TABLE_SHOW_NOTES, {
                      default: "Show Notes",
                    })}
              </Button>
            </Stack>
          </Stack>
        }
      >
        {claimsError ? (
          <Alert severity={"error"}>{String(claimsError)}</Alert>
        ) : (
          <Fragment>
            {(hasDuplicates || hasAppliedDuplicates) && (
              <Alert
                severity={hasAppliedDuplicates ? "success" : "warning"}
                sx={{ mb: 2 }}
                action={
                  <Button
                    variant={hasAppliedDuplicates ? "contained" : "outlined"}
                    color={hasAppliedDuplicates ? "secondary" : "warning"}
                    size={"small"}
                    onClick={handleEditDuplicates}
                    disableElevation
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      ...(hasAppliedDuplicates
                        ? {}
                        : {
                            bgcolor: "white",
                            borderColor: "warning.main",
                            color: "warning.dark",
                            "&:hover": {
                              bgcolor: "warning.light",
                              borderColor: "warning.dark",
                            },
                          }),
                    }}
                  >
                    {hasAppliedDuplicates ? "Edit" : "REVIEW"}
                  </Button>
                }
              >
                {hasAppliedDuplicates ? (
                  (() => {
                    const excludedCount = acceptedDuplicates.reduce(
                      (sum, group) => {
                        const claimIds =
                          group.claim_ids || group.current_claim_ids || [];
                        return sum + Math.max(0, claimIds.length - 1);
                      },
                      0
                    );
                    return `${excludedCount} duplicate${
                      excludedCount !== 1 ? "s" : ""
                    } marked and removed from analysis.`;
                  })()
                ) : (
                  <>
                    {"Potential duplicate claims detected. Click "}
                    <i>{"REVIEW"}</i>
                    {" to examine and manage duplicates."}
                  </>
                )}
              </Alert>
            )}
            {(claims?.length ?? 0) >= CLAIMS_TABLE_MAX_ROWS && (
              <Alert severity={"info"}>
                {`Showing the ${CLAIMS_TABLE_MAX_ROWS} largest claims; smaller claims are not listed.`}
              </Alert>
            )}
            <PaginatedTable
              key={props.mergingClaims ? "merge" : "view"}
              rows={formattedCoverage}
              initialPaginationSize={10}
              pageSizeOptions={[10, 20, 50, 100]}
              sx={{ height: "670px", textWrap: "nowrap" }}
              size={"small"}
              tableHead={({ sort, setSort }) => {
                const Cell = ({ label, width, align, sortColumn }) => {
                  const toggle = !!sortColumn
                    ? () => {
                        if (!sort) {
                          setSort(sortColumn);
                        } else if (sort === sortColumn) {
                          setSort(`-${sortColumn}`);
                        } else if (sort === `-${sortColumn}`) {
                          setSort(null);
                        } else {
                          setSort(sortColumn);
                        }
                      }
                    : () => {};
                  return (
                    <TableCell
                      width={width}
                      onClick={toggle}
                      sx={{
                        verticalAlign: "center",
                        ...(sortColumn ? { cursor: "pointer" } : {}),
                      }}
                    >
                      <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={align}
                      >
                        {sortColumn && sort === sortColumn && (
                          <SortedDescendingIcon
                            fontSize={"small"}
                            sx={{ paddingRight: 1 }}
                          />
                        )}
                        {sortColumn && sort === `-${sortColumn}` && (
                          <SortedAscendingIcon
                            fontSize={"small"}
                            sx={{ paddingRight: 1 }}
                          />
                        )}
                        <Box
                          sx={{
                            whiteSpace: "normal",
                            textAlign: align,
                            lineHeight: 1.2,
                          }}
                        >
                          {label}
                        </Box>
                      </Stack>
                    </TableCell>
                  );
                };

                return (
                  <TableRow sx={{ height: 60 }}>
                    {props.mergingClaims && (
                      <Cell
                        width={100}
                        label={translateLabel(labels.CLAIMS_TABLE_TO_MERGE, {
                          default: "To Merge",
                        })}
                      />
                    )}
                    {!props.mergingClaims && <Cell width={40} />}
                    <Cell
                      width={90}
                      align={"right"}
                      label={translateLabel(labels.CLAIMS_TABLE_DATE, {
                        default: "Date",
                      })}
                      sortColumn={"_DATE_OF_LOSS"}
                    />
                    {rows?.some((row) => row?._GROUP_COUNT > 1) && (
                      <Cell
                        width={70}
                        align={"center"}
                        label={translateLabel(labels.CLAIMS_TABLE_NUM_CLAIMS, {
                          default: "№ Claims",
                        })}
                        sortColumn={"_GROUP_COUNT"}
                      />
                    )}
                    <Cell
                      width={75}
                      align={"center"}
                      label={translateLabel(labels.CLAIMS_TABLE_STATUS, {
                        default: "Status",
                      })}
                      sortColumn={"_OPEN_CLOSED"}
                    />
                    <Cell
                      width={90}
                      align={"right"}
                      label={translateLabel(labels.CLAIMS_TABLE_REPORTED, {
                        default: "Reported",
                      })}
                      sortColumn={"_REPORTED_TOTAL_LOSS"}
                    />
                    {isReportedLossOverridesEnabled && (
                      <Cell
                        width={90}
                        align={"right"}
                        label={"Reported Override"}
                      />
                    )}
                    <Cell
                      width={90}
                      align={"right"}
                      label={translateLabel(labels.CLAIMS_TABLE_ULTIMATE, {
                        default: "Ultimate",
                      })}
                      sortColumn={"_ULTIMATE_INCURRED"}
                    />
                    <Cell
                      width={110}
                      align={"right"}
                      label={
                        isReportedLossOverridesEnabled
                          ? "Ultimate Override"
                          : translateLabel(labels.CLAIMS_TABLE_OVERRIDE, {
                              default: "Override",
                            })
                      }
                    />
                    <Cell
                      width={50}
                      align={"center"}
                      label={translateLabel(labels.CLAIMS_TABLE_LINE, {
                        default: "Line",
                      })}
                      sortColumn={"_COVERAGE"}
                    />
                    <Cell
                      width={90}
                      align={"left"}
                      label={translateLabel(labels.CLAIMS_TABLE_CLAIMANT, {
                        default: "Claimant",
                      })}
                      sortColumn={"_CLAIMANT_NAME"}
                    />
                    <Cell
                      width={160}
                      align={"left"}
                      label={translateLabel(labels.CLAIMS_TABLE_DESCRIPTION, {
                        default: "Description",
                      })}
                      sortColumn={"_DESCRIPTION"}
                    />
                    <Cell
                      width={95}
                      align={"right"}
                      label={translateLabel(labels.CLAIMS_TABLE_DATE_CLOSED, {
                        default: "Date Closed",
                      })}
                      sortColumn={"_"}
                    />
                    <Cell
                      width={150}
                      align={"left"}
                      label={translateLabel(labels.CLAIMS_TABLE_SOURCE, {
                        default: "Source",
                      })}
                      sortColumn={"key"}
                    />
                  </TableRow>
                );
              }}
              tableRow={(row) => (
                <Fragment key={`${row.key}${row.index}`}>
                  <TableRow sx={{ height: 50 }}>
                    {props.mergingClaims && (
                      <TableCell>
                        {row.key === "MANUAL" || inMerge(row) ? (
                          <LockOutlined sx={{ marginLeft: "10px" }} />
                        ) : (
                          <Checkbox
                            checked={props.mergeEntrySelected(row)}
                            onChange={(e) =>
                              props.setMergeStateEntry(row, e.target.checked)
                            }
                          />
                        )}
                      </TableCell>
                    )}
                    {!props.mergingClaims && (
                      <TableCell>
                        {(() => {
                          const excludedKey = props.excludedKeys.includes(
                            row.key
                          );
                          const excluded =
                            excludedKey || isExcluded(props.exclusions, row);
                          if (
                            props.mergedClaims.filter(
                              (mce) =>
                                mce.key === row.key && mce.index === row.index
                            ).length === 0
                          ) {
                            return (
                              <Checkbox
                                checked={!excluded}
                                disabled={excludedKey || row._COVERAGE === null}
                                onChange={(e) =>
                                  props.toggleClaimExclude(
                                    row.key,
                                    row.index,
                                    !e.target.checked
                                  )
                                }
                              />
                            );
                          } else {
                            return <LockOutlined sx={{ marginLeft: "10px" }} />;
                          }
                        })(row)}
                      </TableCell>
                    )}
                    <TableCell align={"right"}>
                      {utils.usDateFormat(row?._DATE_OF_LOSS)}
                    </TableCell>
                    {rows?.some((row) => row?._GROUP_COUNT > 1) && (
                      <TableCell>{row?._GROUP_COUNT}</TableCell>
                    )}
                    <TableCell align={"center"}>
                      <OpenClosedBadge
                        status={row?._OPEN_CLOSED}
                        reportedStatus={row?.["_REPORTED_OPEN_CLOSED"]}
                        overriddenStatus={
                          claimsQuery?.openClosedOverrides?.[row?.key]?.[
                            row?.index
                          ]
                        }
                        onChange={
                          isOpenClosedEditable &&
                          row?.index != null &&
                          row?.key != null
                            ? (newValue) =>
                                props.setClaimOpenClosedOverride(
                                  row?.key,
                                  row?.index,
                                  newValue
                                )
                            : null
                        }
                        onRemoveOverride={
                          isOpenClosedEditable &&
                          row?.index != null &&
                          row?.key != null
                            ? () =>
                                props.removeClaimOpenClosedOverride(
                                  row?.key,
                                  row?.index
                                )
                            : null
                        }
                      />
                    </TableCell>
                    <TableCell align={"right"}>
                      {floatAsDollars(row?._TOTAL_LOSS)}
                    </TableCell>
                    {isReportedLossOverridesEnabled && (
                      <TableCell>
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <PrettyNumberTextField
                            fullWidth
                            type={"text"}
                            hiddenLabel
                            size={"small"}
                            variant={"filled"}
                            inputProps={{ style: { textAlign: "right" } }}
                            onChangeNumber={(x) =>
                              props.updateClaimReportedLossOverride(
                                row.key,
                                row.index,
                                x
                              )
                            }
                            allowClear
                            value={
                              props.reportedLossOverrides?.[row.key]?.[
                                row.index
                              ] ?? ""
                            }
                          />
                        </Box>
                      </TableCell>
                    )}
                    <TableCell align={"right"}>
                      {floatAsDollars(row?._ULTIMATE_INCURRED)}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        <PrettyNumberTextField
                          fullWidth
                          type={"text"}
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          inputProps={{ style: { textAlign: "right" } }}
                          onChangeNumber={(x) =>
                            props.updateClaimOverride(row.key, row.index, x)
                          }
                          allowClear
                          value={props.overrides?.[row.key]?.[row.index] ?? ""}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align={"center"}>
                      <span style={{ fontWeight: "600" }}>
                        {row?._COVERAGE}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={row?._CLAIMANT_NAME || ""}
                        sx={{ justifyContent: "flex-start" }}
                      >
                        <div
                          style={{ overflow: "hidden", whiteSpace: "nowrap" }}
                        >
                          {row?._CLAIMANT_NAME}
                        </div>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={row?._DESCRIPTION || ""}
                        sx={{ justifyContent: "flex-start" }}
                      >
                        <div
                          style={{ overflow: "hidden", whiteSpace: "nowrap" }}
                        >
                          {row?._DESCRIPTION}
                        </div>
                      </Tooltip>
                    </TableCell>
                    <TableCell align={"right"}>
                      {row?._DATE_CLOSED == null
                        ? null
                        : utils.usDateFormat(row?._DATE_CLOSED)}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={`Row ${
                          row?.original_row?.[0] ?? "?"
                        } on sheet '${sourceDetails[row?.key]?.sheet ?? "?"}'`}
                        sx={{ justifyContent: "flex-start" }}
                        arrow
                      >
                        <div
                          style={{ overflow: "hidden", whiteSpace: "nowrap" }}
                        >
                          {sourceDetails[row?.key]?.file ?? "?"}
                        </div>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {isShowingComments && (
                    <TableRow sx={{ height: 50 }}>
                      <TableCell colSpan={3} />
                      <TableCell colSpan={8}>
                        <TextNote
                          title={null}
                          noteKey={`claims.${row.key}.${row.index}`}
                          hasEditButton={false}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )}
            />
          </Fragment>
        )}
      </Component>
    </Fragment>
  );
};

const mapDispatchToProps = {
  toggleClaimExclude: claimsActions.toggleClaimExclude,
  updateClaimOverride: claimsActions.updateClaimOverride,
  updateClaimReportedLossOverride:
    claimsActions.updateClaimReportedLossOverride,
  removeLossYear: claimsActions.removeLossYear,
  addLossYear: claimsActions.addLossYear,
  setClaimOpenClosedOverride: claimsActions.setClaimOpenClosedOverride,
  removeClaimOpenClosedOverride: claimsActions.removeClaimOpenClosedOverride,
};

const mapStateToProps = (state) => ({
  exclusions: state.input.claims.exclusions || {},
  excludedKeys: claimsSelectors.selectExcludedKeys(state),
  overrides: state.input.claims.overrides || {},
  reportedLossOverrides: state.input.claims.reportedLossOverrides || {},
  lossYears: state.input.claims.lossYears || [],
  mergedClaims: claimsSelectors.mergedClaims(state) || [],
});

export default connect(mapStateToProps, mapDispatchToProps)(ClaimsTable);
